import { useMutation, useQuery } from '@apollo/client';
import { Disclosure, Listbox, Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/solid';
import type { JSONContent } from '@tiptap/react';
import { endOfDay, format, startOfDay } from 'date-fns';
import { ModalType } from 'pages/complaints';
import React, { Fragment, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import Avatar from 'components/atoms/avatar';
import Editor, { generate } from 'components/editor';

import classNames from 'utils/classnames';

import {
  addNoteOnUpdate,
  deleteComplaintNoteOne,
  deleteNoteOne,
  getAllComplaints,
  getComplaintById,
  getComplaintCalls,
  getComplaintNotes,
  updateComplaint,
  updateComplaintNote,
} from 'queries/complaints';

import { useBase } from 'providers/base';
import { useOrganization } from 'providers/organization';
import { useSession } from 'providers/session';
import { useUI } from 'providers/ui';

import ComplaintCall from './complaint-call';
import { ComplaintClientSearch, ComplaintMemberSearch } from './search-fields';
import ComplaintClient from './user-profile';

const resolutions = [
  'Open',
  'Closed',
  'Awaiting Response',
  'Requires Input',
  'Response Sent',
];

const isEditorContentEmpty = (value?: string | null) =>
  !value ||
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim().length === 0;

const looksLikeHtml = (value?: string | null) =>
  !!value && /<\/?[a-z][\s\S]*>/i.test(value);

const resolutionToneMap = {
  Open: 'border-rose-200 bg-rose-50 text-rose-700',
  Closed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  'Awaiting Response': 'border-amber-200 bg-amber-50 text-amber-700',
  'Requires Input': 'border-sky-200 bg-sky-50 text-sky-700',
  'Response Sent': 'border-violet-200 bg-violet-50 text-violet-700',
};

const ManageComplaint = ({
  dateState,
  complaintId,
  editMode,
  mode,
  closeModal,
  handleClick,
}: {
  editMode: () => void;
  dateState: any;
  complaintId: string;
  mode: ModalType;
  closeModal: () => void;
  handleClick: ({ type, lead }) => void;
}) => {
  const { claims } = useSession();
  const role = claims['x-hasura-user-id'];
  const [selectedResolution, setSelectedResolution] = useState(null);
  const [updateComplaintMutation] = useMutation(updateComplaint);
  const [addNewNoteOnUpdate] = useMutation(addNoteOnUpdate);
  const [editNoteHandler] = useMutation(updateComplaintNote);

  const [deleteSingleNote] = useMutation(deleteNoteOne);
  const [deleteComplaintNote] = useMutation(deleteComplaintNoteOne);
  const ui = useUI();

  const [note, setNote] = useState(false);
  const [editNote, setEditNote] = useState(false);
  const [isEditNote, setIsEditNote] = useState(false);
  const [editedNote, setEditedNote] = useState(null);
  const { clients } = useBase();
  const { team } = useOrganization();
  const [selected, setSelected] = useState<any | null>(null);
  const [assignTo, setAssignedTo] = useState<any | null>(null);

  const { data: allComplaintData } = useQuery(getComplaintById, {
    variables: {
      id: complaintId,
    },
  });

  const complaintData = allComplaintData?.complaints_by_pk;
  const activeResolution =
    selectedResolution || complaintData?.resolution || resolutions[0];
  const statusTone =
    resolutionToneMap[activeResolution] ||
    'border-slate-200 bg-slate-100 text-slate-700';
  const canCallCustomer = Boolean(complaintData?.user?.contact_number);
  const updatedByName = complaintData?.employee?.full_name
    ? complaintData.employee.full_name
    : complaintData?.employee?.given_name ||
        complaintData?.employee?.family_name
      ? `${complaintData?.employee?.given_name || ''} ${complaintData?.employee?.family_name || ''}`.trim()
      : 'Unknown';
  const assignedToName = complaintData?.assignee?.user
    ? `${complaintData.assignee.user.given_name || ''} ${complaintData.assignee.user.family_name || ''}`.trim()
    : 'Unassigned';

  const {
    register: registercomplaints,
    handleSubmit: handleComplaints,
    reset: resetComplaints,
    setValue: setValue,
    watch,
  } = useForm({
    defaultValues: {
      title: '',
      message: '',
      resolution: '',
      client: '',
      assignee: '',
      note: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const message = watch('message');

  const handleComplaintBodyUpdate = (json: JSONContent) => {
    const html = generate(json);
    setValue('message', isEditorContentEmpty(html) ? '' : html, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    setSelectedResolution(complaintData && complaintData.resolution);
    setSelected(complaintData?.user || null);
    setAssignedTo(complaintData?.assignee || null);

    setTimeout(() => {
      setValue('title', complaintData?.title);
      setValue('message', complaintData?.complaint);
      setValue('resolution', complaintData?.resolution);
      setValue('client', complaintData?.user_id);
      setValue('assignee', complaintData?.assignee?.id);
      setValue('note', '');
    }, 50);
  }, [complaintData, setValue]);

  const { data: complaintNotesData } = useQuery(getComplaintNotes, {
    variables: {
      cid: complaintData?.id,
    },
  });

  const { data: complaintCallsData } = useQuery(getComplaintCalls, {
    variables: {
      cid: complaintData?.id,
    },
  });

  const noteCount = complaintNotesData?.complaint_note?.length || 0;
  const callCount = complaintCallsData?.complaint_call?.length || 0;

  const handleOpenProfile = () => {
    if (!complaintData?.user_id) return;
    ui.setClient(true, complaintData.user_id);
  };

  const handleCallCustomer = () => {
    if (!complaintData?.id || !complaintData?.user?.contact_number) return;

    ui.setCall(true, {
      to: complaintData.user.contact_number,
      complaintId: complaintData.id,
      userId: complaintData.user_id,
      user: complaintData.user,
    });
  };

  const handleUpdate = async (e: string) => {
    const payload = {
      resolution: e,
    };

    await updateComplaintMutation({
      variables: {
        id: complaintData?.id,
        data: payload,
      },
      refetchQueries: [
        {
          query: getAllComplaints,
          variables: {
            end: endOfDay(dateState.controlOption.value.end),
            start: startOfDay(dateState.controlOption.value.start),
          },
        },
      ],
    });
  };

  // edit complaint
  const submitEdit = async (data) => {
    try {
      const payload: { [key: string]: any } = {
        assignee_id: data && data.assignee,
        title: data && data.title,
        complaint: data && data.message,
        resolution: data && data.resolution,
        user_id: data && data.client,
      };

      const result = await updateComplaintMutation({
        variables: {
          id: complaintData.id,
          data: payload,
        },
        refetchQueries: [
          {
            query: getAllComplaints,
            variables: {
              end: endOfDay(dateState.controlOption.value.end),
              start: startOfDay(dateState.controlOption.value.start),
            },
          },
        ],
      });

      if (result) {
        submitNote(data);
        ui.setToast(true, {
          type: 'success',
          title: 'Complaint Updated Successfully',
          message: '',
        });
      }
      resetComplaints();
      closeModal();
    } catch (err) {
      ui.setToast(true, {
        type: 'error',
        title: 'Unable to update Complaint',
        message: `${err}`,
      });
      closeModal();
    }
  };

  const submitEditedNote = async (data) => {
    if (!data) return;

    try {
      setEditNote(false);
      await editNoteHandler({
        variables: {
          id: data.id,
          content: data.content,
        },
        refetchQueries: [
          {
            query: getComplaintNotes,
            variables: {
              cid: complaintData.id,
            },
          },
        ],
      });
      resetComplaints();
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  //delete single note
  const deleteOneNote = async (id: string) => {
    if (confirm('Are you sure you want to delete this note?')) {
      await deleteSingleNote({
        variables: {
          nid: id,
        },
        refetchQueries: [
          {
            query: getComplaintNotes,
            variables: {
              cid: complaintData?.id,
            },
          },
        ],
      });
      await deleteComplaintNote({
        variables: {
          id: id,
        },
        refetchQueries: [
          {
            query: getComplaintNotes,
            variables: {
              cid: complaintData?.id,
            },
          },
        ],
      });
    }
  };

  //submit note
  const submitNote = async (data) => {
    try {
      let result = await addNewNoteOnUpdate({
        variables: {
          data: {
            complaint_id: complaintId,
            note: {
              data: {
                content: data.note,
              },
            },
          },
        },
        refetchQueries: [
          {
            query: getComplaintNotes,
            variables: {
              cid: complaintData?.id,
            },
          },
        ],
      });
      if (result) {
        setNote(false);
      }
      resetComplaints();
    } catch (error) {
      return null;
    }
  };

  return (
    <section className="flex h-full flex-1 flex-col overflow-hidden bg-slate-50">
      <div className="flex flex-1 overflow-scroll flex-col">
        {mode === ModalType.View && (
          <div className="space-y-6 px-4 pb-6 pt-4 xl:px-6">
            {complaintData?.user_id ? (
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <ComplaintClient id={complaintData?.user_id} />
              </div>
            ) : null}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.65fr)_360px]">
              <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <span
                          className={classNames(
                            'inline-flex rounded-full border px-3 py-1 text-sm font-semibold',
                            statusTone,
                          )}
                        >
                          {activeResolution}
                        </span>
                        <h2 className="text-2xl font-semibold text-slate-900">
                          {complaintData?.title || 'Untitled complaint'}
                        </h2>
                      </div>
                      <p className="mt-3 max-w-3xl text-sm text-slate-500">
                        Created{' '}
                        {complaintData?.created
                          ? format(
                              new Date(complaintData.created),
                              "do MMM yyyy 'at' HH:mm",
                            )
                          : 'unknown'}{' '}
                        and due{' '}
                        {complaintData?.due_date
                          ? format(
                              new Date(`${complaintData.due_date}T00:00:00`),
                              'do MMM yyyy',
                            )
                          : 'not set'}
                        .
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 lg:justify-end">
                      <button
                        type="button"
                        onClick={handleCallCustomer}
                        disabled={!canCallCustomer}
                        className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Call customer
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenProfile}
                        disabled={!complaintData?.user_id}
                        className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Open profile
                      </button>
                      <button
                        type="button"
                        onClick={editMode}
                        className="inline-flex items-center whitespace-nowrap rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                      >
                        Edit complaint
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Due by
                      </div>
                      <div className="mt-2 text-lg font-semibold text-slate-900">
                        {complaintData?.due_date
                          ? format(
                              new Date(`${complaintData.due_date}T00:00:00`),
                              'dd MMM yyyy',
                            )
                          : 'Not set'}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Calls linked
                      </div>
                      <div className="mt-2 text-lg font-semibold text-slate-900">
                        {callCount}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Notes
                      </div>
                      <div className="mt-2 text-lg font-semibold text-slate-900">
                        {noteCount}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/80 px-5 py-5">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Nature of complaint
                    </div>
                    {looksLikeHtml(complaintData?.complaint) ? (
                      <div
                        className="prose prose-sm mt-3 max-w-none text-slate-800"
                        dangerouslySetInnerHTML={{
                          __html: complaintData?.complaint || '',
                        }}
                      />
                    ) : (
                      <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">
                        {complaintData?.complaint}
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        Calls
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Every call started here is linked back to this
                        complaint.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCallCustomer}
                      disabled={!canCallCustomer}
                      className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      New call
                    </button>
                  </div>

                  <div className="mt-5">
                    {callCount ? (
                      complaintCallsData.complaint_call.map(({ call, idx }) => {
                        if (!call) return null;
                        return (
                          <ComplaintCall
                            key={call.id || idx}
                            call={call}
                            handleClick={handleClick}
                          />
                        );
                      })
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-sm text-slate-500">
                        No calls are linked to this complaint yet.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        Notes
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Keep the working history on the complaint itself.
                      </p>
                    </div>
                    {!note ? (
                      <button
                        type="button"
                        className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
                        onClick={() => setNote(true)}
                      >
                        Add note
                      </button>
                    ) : null}
                  </div>

                  {note ? (
                    <div className="relative mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <button
                        type="button"
                        className="absolute right-4 top-4 rounded-full border border-slate-200 p-1 text-slate-400 hover:text-slate-600"
                        onClick={() => {
                          setNote(false);
                        }}
                      >
                        <XMarkIcon className="w-3" />
                      </button>
                      <button
                        type="button"
                        className="absolute right-12 top-4 rounded-full border border-slate-200 p-1 text-slate-400 hover:text-slate-700"
                        onClick={handleComplaints(submitNote)}
                      >
                        <CheckIcon className="w-3" />
                      </button>
                      <p className="text-sm font-semibold text-slate-900">
                        New note
                      </p>
                      <textarea
                        name="note"
                        className="mt-3 w-full rounded-2xl border border-slate-200 bg-white p-3 pr-16 text-sm text-slate-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Add detail about what happened, who responded, and what happens next."
                        rows={4}
                        defaultValue={''}
                        {...registercomplaints('note', {
                          required: false,
                        })}
                        spellCheck={true}
                      />
                    </div>
                  ) : null}

                  <div className="mt-5 space-y-3">
                    {noteCount ? (
                      complaintNotesData.complaint_note.map(({ note }) => {
                        if (!note) return null;
                        return (
                          <div
                            className="relative rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4"
                            key={`complaint-note-${note.id}`}
                          >
                            {editNote === note.id ? (
                              <>
                                <CheckCircleIcon
                                  className="absolute right-10 top-4 inline h-4 w-4 cursor-pointer text-purple-500 hover:text-purple-700"
                                  onClick={() => {
                                    submitEditedNote(editedNote);
                                    setIsEditNote(false);
                                    setEditNote(null);
                                  }}
                                />
                                <XCircleIcon
                                  className="absolute right-4 top-4 inline h-4 w-4 cursor-pointer text-purple-500 hover:text-purple-700"
                                  onClick={() => {
                                    setIsEditNote(false);
                                    setEditNote(null);
                                  }}
                                />

                                <textarea
                                  autoFocus
                                  defaultValue={note.content}
                                  onChange={(e) =>
                                    setEditedNote({
                                      content: e.target.value,
                                      id: note.id,
                                    })
                                  }
                                  className={`${isEditNote ? '' : 'hidden'} mt-8 w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500`}
                                />
                              </>
                            ) : (
                              <>
                                <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-3">
                                  <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                                    <Avatar
                                      size={{
                                        width: 6,
                                        height: 6,
                                      }}
                                      layout="fill"
                                      src={
                                        note?.employee?.assets[0]?.asset?.url
                                      }
                                    />
                                    <span>
                                      {note?.employee?.name}{' '}
                                      {note?.employee?.surname}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <PencilSquareIcon
                                      className={`${role === note.user ? '' : 'hidden'} h-4 w-4 cursor-pointer text-purple-500 hover:text-purple-700`}
                                      onClick={() => {
                                        setEditNote(note.id);
                                        setIsEditNote(true);
                                      }}
                                    />
                                    <TrashIcon
                                      className={`${role === note.user ? '' : 'hidden'} h-4 w-4 cursor-pointer text-purple-500 hover:text-red-400`}
                                      onClick={() => {
                                        deleteOneNote(note.id);
                                      }}
                                    />
                                  </div>
                                </div>
                                <p className="pt-3 text-sm text-slate-700">
                                  {note.content}
                                </p>
                              </>
                            )}

                            {note?.updated_at && (
                              <p className="pt-3 text-right text-xs text-slate-400">
                                Last updated{' '}
                                {format(
                                  new Date(note.updated_at),
                                  "do MMM yyyy 'at' HH:mm",
                                )}
                              </p>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-sm text-slate-500">
                        No notes have been added to this complaint yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Resolution
                  </div>
                  <p className="mt-2 text-sm text-slate-500">
                    Update the live complaint status without leaving this view.
                  </p>
                  <div className="mt-4">
                    <Listbox
                      value={selectedResolution}
                      onChange={(e) => {
                        setSelectedResolution(e);
                        setValue('resolution', e);
                        handleUpdate(e);
                      }}
                    >
                      <Listbox.Button className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-800 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500">
                        {activeResolution}
                      </Listbox.Button>

                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <div className="relative m-0 bg-white">
                          <Listbox.Options className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-2xl border border-slate-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                            {resolutions.map((resolution) => (
                              <Listbox.Option
                                key={resolution}
                                value={resolution}
                                onChange={(e) =>
                                  setSelectedResolution(
                                    (selectedResolution) =>
                                      selectedResolution === e,
                                  )
                                }
                                className={({ active }) =>
                                  `relative cursor-default select-none rounded-xl px-4 py-3 ${
                                    active
                                      ? 'bg-purple-500 text-white'
                                      : 'text-slate-900'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <div className="flex items-center justify-between gap-3">
                                    <span
                                      className={`block truncate ${
                                        selected ? 'font-medium' : 'font-normal'
                                      }`}
                                    >
                                      {resolution}
                                    </span>
                                    {selected ? (
                                      <CheckIcon
                                        className="h-5 w-5 text-current"
                                        aria-hidden="true"
                                      />
                                    ) : null}
                                  </div>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </div>
                      </Transition>
                    </Listbox>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Complaint details
                  </div>
                  <dl className="mt-4 space-y-4 text-sm">
                    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
                      <dt className="text-slate-500">Created</dt>
                      <dd className="text-right font-medium text-slate-900">
                        {complaintData?.created
                          ? format(
                              new Date(complaintData.created),
                              "do MMM yyyy 'at' HH:mm",
                            )
                          : 'Unknown'}
                      </dd>
                    </div>
                    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
                      <dt className="text-slate-500">Due by</dt>
                      <dd className="text-right font-medium text-slate-900">
                        {complaintData?.due_date
                          ? format(
                              new Date(`${complaintData.due_date}T00:00:00`),
                              'do MMM yyyy',
                            )
                          : 'Not set'}
                      </dd>
                    </div>
                    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
                      <dt className="text-slate-500">Last updated</dt>
                      <dd className="text-right font-medium text-slate-900">
                        {complaintData?.updated
                          ? format(
                              new Date(complaintData.updated),
                              "do MMM yyyy 'at' HH:mm",
                            )
                          : 'Unknown'}
                      </dd>
                    </div>
                    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
                      <dt className="text-slate-500">Updated by</dt>
                      <dd className="flex items-center gap-2 text-right font-medium text-slate-900">
                        <Avatar
                          size={{
                            width: 6,
                            height: 6,
                          }}
                          layout="fill"
                          src={
                            complaintData?.employee?.user_assets[0]?.asset?.url
                          }
                        />
                        <span>{updatedByName}</span>
                      </dd>
                    </div>
                    <div className="flex items-start justify-between gap-4">
                      <dt className="text-slate-500">Assigned to</dt>
                      <dd className="flex items-center gap-2 text-right font-medium text-slate-900">
                        {complaintData?.assignee?.user ? (
                          <Avatar
                            size={{
                              width: 6,
                              height: 6,
                            }}
                            layout="fill"
                            src={
                              complaintData?.assignee?.user?.user_assets[0]
                                ?.asset?.url
                            }
                          />
                        ) : null}
                        <span>{assignedToName}</span>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}

        {mode === ModalType.Edit && (
          <form className="space-y-4">
            <ComplaintClient id={complaintData?.user_id} />
            <div className="bg-white space-y-4 p-4">
              <div className="flex flex-col-2 space-y-2">
                <div className="flex-wrap w-full flex-col space-y-3">
                  <div className="py-2">
                    <span className="font-semibold text-sm xl:text-sm">
                      Complaint Title
                    </span>
                    <input
                      type="text"
                      name="title"
                      {...registercomplaints('title', { required: true })}
                      className="transition-all w-full text-left bg-white rounded-lg shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white  border border-gray-300 focus-visible:ring-offset-purple-500 focus-visible:ring-offset-2 focus-visible:border-purple-500 py-1.5 xl:py-3 text-xs "
                    />
                  </div>
                  <div className="border-t py-2">
                    <span className="font-semibold text-sm xl:text-sm">
                      Complaint Message:
                    </span>
                    <input
                      type="hidden"
                      {...registercomplaints('message', { required: true })}
                    />
                    <div className="mt-2 rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <Editor
                        key={`complaint-editor-${complaintData?.id || 'pending'}`}
                        content={message || complaintData?.complaint || null}
                        shortMenu
                        onUpdate={handleComplaintBodyUpdate}
                        placeholder="Describe the complaint clearly for the assignee."
                        className="min-h-[200px] max-w-none prose-sm"
                        classes="overflow-hidden rounded-2xl"
                      />
                    </div>
                  </div>
                  <div className="border-t py-2">
                    <span className="font-semibold text-xs xl:text-sm">
                      Resolution Status
                    </span>
                    <Listbox
                      value={selectedResolution}
                      onChange={(e) => {
                        setSelectedResolution(e);
                        setValue('resolution', e);
                      }}
                    >
                      <Listbox.Button className="transition-all w-full text-left pl-2 bg-white rounded-lg shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white  border border-gray-300 focus-visible:ring-offset-purple-500 focus-visible:ring-offset-2 focus-visible:border-purple-500 py-1.5 xl:py-3 text-xs ">
                        {selectedResolution}
                      </Listbox.Button>

                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <div className="relative m-0 bg-white">
                          <Listbox.Options className="absolute z-2 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {resolutions.map((resolution) => (
                              <Listbox.Option
                                key={resolution}
                                value={resolution}
                                onChange={(e) =>
                                  setSelectedResolution(
                                    (selectedResolution) =>
                                      selectedResolution === e,
                                  )
                                }
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 rounded-md ${
                                    active
                                      ? 'bg-purple-500 text-white'
                                      : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate rounded-full ${
                                        selected ? 'font-medium' : 'font-normal'
                                      }`}
                                    >
                                      {resolution}
                                    </span>
                                    {selected ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-purple-800">
                                        <CheckIcon
                                          className="h-5 w-5"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </div>
                      </Transition>
                    </Listbox>
                  </div>
                  <input
                    type="hidden"
                    {...registercomplaints('client', { required: true })}
                  />
                  <ComplaintClientSearch
                    clients={clients || []}
                    selected={selected}
                    onChange={(value) => {
                      setSelected(value);
                      setValue('client', value.id, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    onClear={() => {
                      setSelected(null);
                      setValue('client', '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  />

                  <input
                    type="hidden"
                    {...registercomplaints('assignee', { required: false })}
                  />
                  <ComplaintMemberSearch
                    members={team || []}
                    selected={assignTo}
                    onChange={(value) => {
                      setAssignedTo(value);
                      setValue('assignee', value.id, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    onClear={() => {
                      setAssignedTo(null);
                      setValue('assignee', '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  />
                  <div className=" relative flex flex-col justify-between border-b">
                    <Disclosure>
                      {({ open }) => (
                        <>
                          <Disclosure.Button
                            className={
                              'flex justify-between py-3 text-sm text-gray-500'
                            }
                          >
                            Notes (
                            {complaintNotesData &&
                              complaintNotesData.complaint_note.length}
                            )
                            <ChevronUpIcon
                              className={`${
                                open ? 'rotate-180 transform' : ''
                              } self-left h-5 w-5 text-purple-500 ml-4`}
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel>
                            {complaintNotesData ? (
                              <div>
                                {complaintNotesData.complaint_note.map(
                                  ({ note }) => {
                                    if (!note) return null;
                                    return (
                                      <div
                                        className="relative flex-col my-2 pt-3 w-full items-center shadow-md px-4 py-1.5 border border-gray-300 text-xs leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 justify-between"
                                        key={`complaint-note-${note.id}`}
                                      >
                                        {editNote === note.id ? (
                                          <>
                                            <CheckCircleIcon
                                              className="absolute right-6 top-1 inline mr-3 w-4 h-4 cursor-pointer text-purple-500 hover:text-purple-700"
                                              onClick={() => {
                                                submitEditedNote(editedNote);
                                                setIsEditNote(false);
                                                setEditNote(null);
                                              }}
                                            />
                                            <XCircleIcon
                                              className="absolute right-1 top-1 inline mr-3 w-4 h-4 cursor-pointer text-purple-500 hover:text-purple-700"
                                              onClick={() => {
                                                setIsEditNote(false);
                                                setEditNote(null);
                                              }}
                                            />

                                            <textarea
                                              autoFocus
                                              defaultValue={note.content}
                                              onChange={(e) =>
                                                setEditedNote({
                                                  content: e.target.value,
                                                  id: note.id,
                                                })
                                              }
                                              className={`${
                                                isEditNote ? '' : 'hidden'
                                              } transition-all px-1 py-1 text-left w-10/12 bg-white shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white rounded border border-gray-300 focus-visible:ring-offset-purple-500 focus-visible:ring-offset-2 focus-visible:border-purple-500  text-xs`}
                                            />
                                          </>
                                        ) : (
                                          <>
                                            <div className="border-b pb-1">
                                              <PencilSquareIcon
                                                className={`${
                                                  role === note.user
                                                    ? ''
                                                    : 'hidden'
                                                } absolute right-6 top-2 inline mr-3 w-4 h-4 cursor-pointer text-purple-500 hover:text-purple-700`}
                                                onClick={() => {
                                                  setEditNote(note.id);
                                                  setIsEditNote(true);
                                                }}
                                              />
                                              <TrashIcon
                                                className={`${
                                                  role === note.user
                                                    ? ''
                                                    : 'hidden'
                                                }  absolute right-1 top-2 inline mr-3 w-4 h-4 cursor-pointer text-purple-500 hover:text-red-400`}
                                                onClick={() => {
                                                  deleteOneNote(note.id);
                                                }}
                                              />
                                              <div>
                                                <Avatar
                                                  size={{
                                                    width: 6,
                                                    height: 6,
                                                  }}
                                                  layout="fill"
                                                  src={
                                                    note?.employee?.assets[0]
                                                      ?.asset?.url
                                                  }
                                                />
                                                {note?.employee?.name}{' '}
                                                {note?.employee?.surname}
                                              </div>
                                            </div>
                                            <p className="text-xs pt-1 w-5/6">
                                              {note.content}
                                            </p>
                                          </>
                                        )}

                                        {note && note.updated_at && (
                                          <p className="text-xs text-right pt-2 text-gray-400">
                                            Last Updated:{' '}
                                            {format(
                                              new Date(note.updated_at),
                                              "do MMM yyyy 'at' HH:mm",
                                            )}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            ) : (
                              <p className=" py-3  text-sm xl:text-sm text-gray-800">
                                No notes added to complaint
                              </p>
                            )}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  <div className="relative z-0 w-full">
                    {note ? (
                      <div className="relative border-t mt-3 pt-3 flex flex-col items-start flex-1">
                        <button
                          type="button"
                          className="transition-all absolute top-3 right-0 rounded-full text-gray-400 p-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white  border border-gray-300 focus-visible:ring-offset-purple-500 focus-visible:ring-offset-2 focus-visible:border-purple-500"
                          onClick={() => {
                            setNote(false);
                          }}
                        >
                          <XMarkIcon className="w-3" />
                        </button>
                        <button
                          className="transition-all absolute top-3 right-6 rounded-full text-gray-400 p-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white  border border-gray-300 focus-visible:ring-offset-purple-500 focus-visible:ring-offset-2 focus-visible:border-purple-500"
                          onClick={handleComplaints(submitNote)}
                        >
                          <CheckIcon className="w-3" />
                        </button>
                        <p className="font-semibold text-sm text-gray-700">
                          Note
                        </p>
                        <textarea
                          name="note"
                          className="transition-all mt-3 p-2 pr-8 bg-white rounded-lg shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white  border border-gray-300 focus-visible:ring-offset-purple-500 focus-visible:ring-offset-2 focus-visible:border-purple-500 text-xs w-full"
                          placeholder="Add note"
                          rows={4}
                          defaultValue={''}
                          {...registercomplaints('note', {
                            required: false,
                          })}
                          spellCheck={true}
                        />
                      </div>
                    ) : (
                      <div className="flex space-x-2 mt-3 pt-3 flex-1">
                        <button
                          type="button"
                          className="transition-all rounded-md border border-gray-300 bg-white py-2 px-4 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex-1"
                          onClick={() => setNote(true)}
                        >
                          Add a note
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      <div className="flex z-0 w-full p-2 place-content-end">
        {mode === ModalType.View ? (
          <button className="panel-primary-button" onClick={editMode}>
            Edit
          </button>
        ) : (
          <>
            <button className="panel-secondary-button" onClick={editMode}>
              Cancel
            </button>

            <button
              className="panel-primary-button"
              onClick={handleComplaints(submitEdit)}
            >
              Save
            </button>
          </>
        )}
      </div>
    </section>
  );
};

export default ManageComplaint;
